import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';

export function RequestsSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { labels: expLabels } = useExpenditureLabels();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/api/admin/request-settings');
      setSettings(res.data.settings || {
        activate_request_approval: false,
        auto_generate_request: false,
        mandatory_fields: { header: [], line: [] }
      });
    } catch (err) {
      setSettings({
        activate_request_approval: false,
        auto_generate_request: false,
        mandatory_fields: { header: [], line: [] }
      });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/request-settings', settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const toggleMandatoryField = (section, field) => {
    setSettings(prev => {
      const fields = prev.mandatory_fields[section] || [];
      const newFields = fields.includes(field)
        ? fields.filter(f => f !== field)
        : [...fields, field];
      return {
        ...prev,
        mandatory_fields: { ...prev.mandatory_fields, [section]: newFields }
      };
    });
  };

  if (loading) return <LoadingSpinner />;

  // Header fields
  const headerFields = [
    { id: 'account', label: expLabels.exp_code_1 || 'Account', icon: '📊' },
    { id: 'category_code', label: 'Category Code', icon: '📁' },
    { id: 'delivery_code', label: 'Delivery Code', icon: '📦' },
    { id: 'delivery_to', label: 'Delivery To', icon: '📍' },
    { id: 'department_code', label: expLabels.exp_code_3 || 'Department Code', icon: '🏢' },
    { id: 'document_type', label: 'Document Type', icon: '📄' },
    { id: 'incoterms', label: 'Incoterms', icon: '🚢' },
    { id: 'instructions', label: 'Instructions', icon: '📝' },
    { id: 'ref_num', label: 'Ref Num', icon: '🔢' },
    { id: 'request_description', label: 'Request Description', icon: '📋' },
    { id: 'restaurant', label: expLabels.exp_code_2 || 'Restaurant', icon: '🍽️' },
    { id: 'ship_via', label: 'Ship Via', icon: '🚚' },
  ];

  // Line fields
  const lineFields = [
    { id: 'cost_centre', label: expLabels.cost_centre || 'Cost Centre', icon: '💰' },
    { id: 'due_date', label: 'Due Date', icon: '📅' },
    { id: 'expense_type', label: 'Expense Type', icon: '💳' },
    { id: 'gl', label: expLabels.exp_code_4 || 'GL', icon: '📒' },
    { id: 'nominal_code', label: expLabels.exp_code_6 || 'Nominal Code', icon: '🔢' },
    { id: 'property_code', label: expLabels.exp_code_5 || 'Property Code', icon: '🏠' },
    { id: 'supplier', label: 'Supplier', icon: '🏭' },
    { id: 'suppliers_part_number', label: 'Suppliers Part Number', icon: '🔩' },
    { id: 'unit', label: 'Unit', icon: '📏' },
  ];

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold mb-1">Requests</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">Request Settings</p>

      {/* Section 1: Activate Request Approval */}
      <div className="border rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">1</span>
          Activate Request Approval
        </h3>
        
        <p className="text-sm text-muted-foreground mb-4">
          Before processing a Request into a Purchase Order or an RFQ, you can insist that each Request 
          requires a formal sign-off by the nominated Approver.
        </p>

        <div className="flex items-center gap-4">
          <Label className="w-48 text-sm">Activate Request Approval</Label>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.activate_request_approval}
              onCheckedChange={(v) => setSettings(p => ({ ...p, activate_request_approval: v }))}
            />
            <span className={`text-sm font-medium ${settings.activate_request_approval ? 'text-green-600' : 'text-muted-foreground'}`}>
              {settings.activate_request_approval ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Section 2: Auto Generate Request */}
      <div className="border rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">2</span>
          Auto Generate Request
        </h3>
        
        <p className="text-sm text-muted-foreground mb-4">
          When any Quick Order is issued, you can select whether system should generate Auto Request 
          for that corresponding Order.
        </p>

        <div className="flex items-center gap-4">
          <Label className="w-48 text-sm">Auto Generate Request</Label>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.auto_generate_request}
              onCheckedChange={(v) => setSettings(p => ({ ...p, auto_generate_request: v }))}
            />
            <span className={`text-sm font-medium ${settings.auto_generate_request ? 'text-green-600' : 'text-muted-foreground'}`}>
              {settings.auto_generate_request ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Section 3: Set Mandatory Fields */}
      <div className="border rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">3</span>
          Set Mandatory Fields
        </h3>
        
        <p className="text-sm text-muted-foreground mb-6">
          Tick the appropriate checkbox to ensure that a specific field on a request is mandatory and must have a value.
        </p>

        <div className="grid grid-cols-2 gap-8">
          {/* Request Header */}
          <div>
            <h4 className="font-medium text-xs mb-3 text-muted-foreground uppercase tracking-wide">Request Header</h4>
            <div className="grid grid-cols-4 gap-2">
              {headerFields.map(field => {
                const isSelected = (settings.mandatory_fields?.header || []).includes(field.id);
                return (
                  <button
                    key={field.id}
                    onClick={() => toggleMandatoryField('header', field.id)}
                    className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all ${
                      isSelected ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 relative ${
                      isSelected ? 'bg-primary/20' : 'bg-muted'
                    }`}>
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-[10px]">✓</span>
                        </div>
                      )}
                      <span className="text-lg">{field.icon}</span>
                    </div>
                    <span className="text-[10px] text-center leading-tight">{field.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Request Line */}
          <div>
            <h4 className="font-medium text-xs mb-3 text-muted-foreground uppercase tracking-wide">Request Line</h4>
            <div className="grid grid-cols-4 gap-2">
              {lineFields.map(field => {
                const isSelected = (settings.mandatory_fields?.line || []).includes(field.id);
                return (
                  <button
                    key={field.id}
                    onClick={() => toggleMandatoryField('line', field.id)}
                    className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all ${
                      isSelected ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 relative ${
                      isSelected ? 'bg-primary/20' : 'bg-muted'
                    }`}>
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-[10px]">✓</span>
                        </div>
                      )}
                      <span className="text-lg">{field.icon}</span>
                    </div>
                    <span className="text-[10px] text-center leading-tight">{field.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
